import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Eye, Loader2, Clock, Check, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';

/**
 * Admin → Membership Management: the View-As control on a member's card.
 *
 * It asks, it does not take. The first click sends the member a request they have to accept;
 * only then does the button open their account. His rule: "This is a help feature and not a
 * stalking feature."
 *
 * `live` comes from the page's single /view-as/summary call — the button never fetches for
 * itself, because a 45-card list would mean 45 requests.
 */
export const ViewAsButton = ({ member, live, onChanged }) => {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [busy, setBusy] = useState(false);
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState('');

  if (member.role === 'admin') return null;   // admins are never viewable

  const ask = async () => {
    if (reason.trim().length < 3) return toast.error('Say what you are helping with');
    setBusy(true);
    try {
      await axios.post(`${API}/view-as/request`, { user_id: member.id, reason: reason.trim() },
                       { headers });
      toast.success(`Asked ${member.full_name || member.email} for permission`);
      setAsking(false);
      setReason('');
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't send that request");
    } finally { setBusy(false); }
  };

  const enter = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/view-as/${live.grant_id}/start`, {}, { headers });
      // The admin's own session is kept aside under its own keys, so Exit is just a swap back.
      localStorage.setItem('admin_token', token);
      localStorage.setItem('admin_user', localStorage.getItem('user') || '');
      localStorage.setItem('view_as', JSON.stringify({
        name: res.data.viewing_as, grant_id: res.data.grant_id,
      }));
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      window.location.href = '/dashboard';
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't open that account");
      setBusy(false);
    }
  };

  if (live?.status === 'approved') {
    return (
      <Button size="sm" onClick={enter} disabled={busy} className="text-xs gap-1"
              data-testid={`view-as-enter-${member.id}`}>
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Eye className="w-3 h-3" />}
        View As
      </Button>
    );
  }

  if (live?.status === 'pending') {
    return (
      <Button size="sm" variant="outline" disabled className="text-xs gap-1 opacity-70"
              data-testid={`view-as-pending-${member.id}`}>
        <Clock className="w-3 h-3" /> Awaiting their OK
      </Button>
    );
  }

  if (asking) {
    return (
      <div className="flex items-center gap-1" data-testid={`view-as-ask-${member.id}`}>
        <input
          autoFocus
          className="text-xs px-2 py-1 rounded border border-gray-600 bg-transparent"
          placeholder="What are you helping with?"
          value={reason}
          onChange={e => setReason(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') ask(); if (e.key === 'Escape') setAsking(false); }}
          data-testid={`view-as-reason-${member.id}`}
        />
        <Button size="sm" onClick={ask} disabled={busy} className="text-xs px-2"
                data-testid={`view-as-send-${member.id}`}>
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
        </Button>
        <Button size="sm" variant="outline" onClick={() => setAsking(false)} className="text-xs px-2">
          <X className="w-3 h-3" />
        </Button>
      </div>
    );
  }

  return (
    <Button size="sm" variant="outline" onClick={() => setAsking(true)} className="text-xs gap-1"
            title="Ask this member for permission to see their account"
            data-testid={`view-as-ask-btn-${member.id}`}>
      <Eye className="w-3 h-3" /> Ask to view
    </Button>
  );
};

export default ViewAsButton;
